import { useEffect, useState } from "react";
import { Button, Input } from "reactstrap";

import AppDrawer, { DrawerError } from "../../Drawers/AppDrawer";
import { FileInterface } from "../../../types/RecordInterfaces";
import { DrawerProps } from "../../Drawers/DrawerTypes";
import AppSpinner from "../../../elements/AppSpinner";
import useTranslatePdf from "./useTranslatePdf";

interface Props extends DrawerProps {
  file: FileInterface | null;
  handleCapture: (
    obj: { files: any[] },
    fileName?: string,
    onSuccess?: () => void
  ) => void;
  submitting: boolean;
}

export default function TranslateDrawer({
  open,
  onClose,
  file,
  handleCapture,
  submitting,
}: Props) {
  const [page, setPage] = useState<"Setup" | "Results">("Setup");
  const [error, setError] = useState<DrawerError>();
  const [translatedFileName, setTranslatedFileName] = useState(file?.Name);
  const [language, setLanguage] = useState("");
  const [translatedFile, setTranslatedFile] = useState("");
  const { translatePdf, translatingPdf } = useTranslatePdf();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setTranslatedFileName(file?.Name || "");
  }, [file?.Name]);

  return (
    <AppDrawer
      open={open}
      onClose={handleClose}
      size={page === "Setup" ? "sm" : "full"}
      error={error}
      body={
        <>
          {page === "Setup" && (
            <>
              <h4 className="mt--2">Translation File Name</h4>
              <Input
                value={translatedFileName}
                onChange={(e) => setTranslatedFileName(e.target.value)}
                className="mb-4"
              />
              <h4>Target Language</h4>
              <Input
                placeholder="Ex: Spanish"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
              />
            </>
          )}
          {page === "Results" && (
            <div className="d-flex h-100 gap-2">
              <div className="w-100 h-100">
                <h3>Source Document</h3>
                <object
                  data={`${file?.URL || file?.PublicURL}#toolbar=0`}
                  type="application/pdf"
                  style={{ width: "100%", height: "100%" }}
                  className="d-flex justify-content-center align-items-center"
                ></object>
              </div>
              <div className="w-100 h-100">
                <h3>Translated Document</h3>
                <object
                  data={`${translatedFile}#toolbar=0`}
                  type="application/pdf"
                  style={{ width: "100%", height: "100%" }}
                  className="d-flex justify-content-center align-items-center"
                ></object>
              </div>
            </div>
          )}
        </>
      }
      headerTitle={"Brie Translate"}
      headerDescription="Translate document into another language"
      headerOptions={
        <>
          {page === "Setup" && (
            <Button
              color="primary"
              size="sm"
              onClick={onTranslate}
              disabled={!translatedFileName || !language || translatingPdf}
              style={{ width: 100 }}
            >
              {translatingPdf ? (
                <AppSpinner color="white" size="sm" />
              ) : (
                "Translate"
              )}
            </Button>
          )}
          {page === "Results" && (
            <>
              <Button size="sm" onClick={() => window.open(translatedFile)}>
                Download Translation
              </Button>
              <Button
                color="primary"
                size="sm"
                onClick={onSave}
                disabled={loading || submitting}
                style={{ width: 150 }}
              >
                {loading || submitting ? (
                  <AppSpinner color="white" size="sm" />
                ) : (
                  "Save Document"
                )}
              </Button>
            </>
          )}
        </>
      }
    />
  );

  function handleClose() {
    setPage("Setup");
    setLanguage("");
    setError(undefined);
    onClose();
  }

  function onTranslate() {
    const fileURL = file?.URL || file?.PublicURL;

    if (!fileURL) {
      return setError({
        message: "An unexpected error occurred.",
        onClear: () => setError(undefined),
      });
    }

    setError(undefined);

    translatePdf({
      fileURL: fileURL,
      language: language,
      onSuccess: (resultFile: any) => {
        setTranslatedFile(resultFile);
        setPage("Results");
      },
      onError: () => {
        setError({
          message: "An error occurred while translating your file.",
          onClear: () => setError(undefined),
        });
      },
    });
  }

  async function onSave() {
    setLoading(true);

    try {
      // Fetch the data from the Object URL
      const response = await fetch(translatedFile);

      // Convert the response to a Blob
      const blob = await response.blob();

      const fileToSave = new File(
        [blob],
        translatedFileName || file?.Name || "translation.pdf",
        { type: "application/pdf" }
      );

      await handleCapture(
        { files: [fileToSave] },
        translatedFileName,
        handleClose
      );
    } catch {}

    setLoading(false);
  }
}
